/**
 * The widget's own participant-facing text, per interview language — the
 * TypeScript counterpart of the consoles' `webui/static/shared/i18n.js`.
 *
 * The interviewer's turns arrive already in the interview's language (the LLM
 * writes them; the consent gate's strings come from domain/i18n.py). This is
 * everything the widget paints itself. The language is the interview's: the
 * survey's own (GET /surveys/{id}/branding → `language`), or the visitor's
 * pick on the welcome splash (POST /interviews/{id}/language) — never the
 * browser's.
 *
 * Several widgets can share a page, so nothing here is global state: each
 * instance holds its language and calls `translate(code, key, vars)`.
 * tests/test_i18n.py checks every table has every key and that each
 * `consent_suffix` matches the server's.
 */

export interface Language {
  /** domain/i18n.LANGUAGES name — what the API takes and returns. */
  name: string;
  code: string;
  /** The language's own name for itself, as the picker shows it. */
  native: string;
}

export const LANGUAGES: Language[] = [
  ["English", "en", "English"],
  ["Spanish", "es", "Español"],
  ["French", "fr", "Français"],
  ["German", "de", "Deutsch"],
  ["Italian", "it", "Italiano"],
  ["Portuguese", "pt", "Português"],
  ["Russian", "ru", "Русский"],
  ["Japanese", "ja", "日本語"],
  ["Korean", "ko", "한국어"],
  ["Chinese", "zh", "中文"],
].map(([name, code, native]) => ({ name: name!, code: code!, native: native! }));

export const DEFAULT_LANGUAGE: Language = LANGUAGES[0]!;

type Table = Record<string, string>;

const STRINGS: Record<string, Table> = {
  en: {
    interview: "Interview",
    open_interview: "Open interview",
    close_interview: "Close interview",
    type_answer: "Type your answer…",
    your_answer: "Your answer",
    send: "Send",
    hold_to_talk: "Hold to talk",
    language: "Language",
    language_error: "Couldn't change the language. Please try again.",
    starting: "Starting your interview…",
    start_failed: "Couldn't start this interview. Please try again.",
    progress: "Question {n} of {total}",
    continue: "Continue",
    start_interview: "Start interview",
    listen: "Listen",
    listen_stop: "Stop",
    listen_failed: "We couldn't play the welcome out loud — it's all written above.",
    voice_intro: "An AI interviewer will ask you a short set of questions out loud. Answer by speaking.",
    prefer_not: "Prefer not to answer",
    show_more: "Show more",
    n_selected: "{n} selected",
    tap_then_send: "Tap all that apply, then send",
    choose_one: "Choose one",
    pick_number: "Pick a number",
    pick_top: "Pick your top choice first",
    ranked_so_far: "Ranked so far: {list} — pick #{n}",
    card_where: "Where does “{card}” belong? ({n} of {total})",
    study_image: "study image",
    upload_image: "Upload an image",
    uploading: "Uploading…",
    upload_failed: "Upload failed — try again or paste a link.",
    didnt_catch: "Didn't catch that — try again.",
    heard: "heard “{raw}” → “{answer}”",
    speaking: "Speaking…",
    listening: "Listening… tap to finish",
    listening_hands_free: "Listening — just talk. Tap the mic to mute.",
    mic_hands_free: "Turn the microphone on or off",
    transcribing: "Transcribing…",
    voice_unavailable: "Voice unavailable.",
    voice_hiccup: "Voice hiccup — try again.",
    mic_unavailable: "Microphone unavailable — check permissions.",
    recording_broken: "Recording has stopped working — the interview continues and your answers are still saved as text.",
    "rec.notice.title": "Recording",
    "rec.notice.audio_required": "Your audio and the interviewer's will be recorded. This is required to take part.",
    "rec.notice.video_required": "Your camera and the interviewer's video will be recorded too. This is required to take part.",
    "rec.notice.audio_optional": "Record audio (yours and the interviewer's)",
    "rec.notice.video_optional": "Record video (your camera and the interviewer's)",
    "rec.notice.video_needs_audio": "Video can only be recorded together with audio.",
    "rec.notice.continue": "Continue",
    "rec.notice.decline": "I don't agree",
    "rec.indicator.audio": "This interview is being recorded — your audio and the interviewer's are saved for the research team.",
    "rec.indicator.video": "This interview is being recorded — your camera and microphone, and the interviewer's video and audio, are saved for the research team.",
    "rec.indicator.paused": "Recording paused — it resumes when the interview continues.",
    conversation_ended: "This conversation has ended.",
    message_failed: "Message failed — please try again.",
    "reprompt.options": "Let's try that again — choose one of the options below.",
    "reprompt.multi_select": "Let's try that again — choose from the options below.",
    "reprompt.scale": "Let's try that again — pick a point on the scale below.",
    "reprompt.ranking": "Let's try that again — put every item in order below.",
    "reprompt.card_sort": "Let's try that again — put every item into one of the groups.",
    "done.title": "That's a wrap — thank you.",
    "done.text": "Your words go straight to the team building this.",
    "done.all_questions_asked": "You answered every question. Your words go straight to the team building this.",
    "done.consent_declined": "No problem — thanks for stopping by.",
    "done.disqualified": "Thanks for your interest! This particular study wasn't the right fit.",
    "done.global_turn_cap": "That's all for now — thank you for your time.",
    "closed.survey_full.title": "This study is closed.",
    "closed.survey_full.text": "It has reached its maximum number of responses. Thank you for your interest!",
    "closed.start_failed.title": "We couldn't run this interview.",
    "closed.start_failed.text": "We hit a technical problem on our end. Please try again in a few minutes, or contact the study organizer if it keeps happening.",
    "closed.quota_denied.title": "This study isn't accepting responses.",
    "closed.quota_denied.text": "It isn't taking new responses right now. Thank you for your interest!",
    copy_link: "Copy link",
    copied: "Copied",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Contact details",
    "contact.name": "Name",
    "contact.optional": "optional",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "contact.email_placeholder": "you@example.com",
    "contact.phone_placeholder": "+1 555 123 4567",
    "contact.submit": "Share details",
    "contact.decline": "Prefer not to share",
    "contact.privacy": "Stored separately from your answers.",
    "contact.shared": "Details shared",
    "contact.declined": "Preferred not to share",
    "contact.err.invalid_name": "That name is too long.",
    "contact.err.invalid_email": "Enter a valid email address.",
    "contact.err.invalid_phone": "Enter a valid phone number (7–15 digits).",
    "contact.err.missing_email": "An email address is needed to continue.",
    "contact.err.missing_phone": "A phone number is needed to continue.",
    "contact.err.empty": "Enter an email address or phone number.",
    "contact.err.required": "Contact details are needed to take part in this study.",
    "contact.err.failed": "Your details couldn't be saved. Please try again.",
    consent_suffix: "Please reply with Yes or No (or the number):",
  },

  es: {
    interview: "Entrevista",
    open_interview: "Abrir entrevista",
    close_interview: "Cerrar entrevista",
    type_answer: "Escribe tu respuesta…",
    your_answer: "Tu respuesta",
    send: "Enviar",
    hold_to_talk: "Mantén pulsado para hablar",
    language: "Idioma",
    language_error: "No se pudo cambiar el idioma. Inténtalo de nuevo.",
    starting: "Iniciando tu entrevista…",
    start_failed: "No se pudo iniciar esta entrevista. Inténtalo de nuevo.",
    progress: "Pregunta {n} de {total}",
    continue: "Continuar",
    start_interview: "Empezar la entrevista",
    listen: "Escuchar",
    listen_stop: "Detener",
    listen_failed: "No hemos podido leer la bienvenida en voz alta; la tienes escrita arriba.",
    voice_intro: "Un entrevistador con IA te hará en voz alta unas pocas preguntas. Responde hablando.",
    prefer_not: "Prefiero no responder",
    show_more: "Ver más",
    n_selected: "{n} seleccionadas",
    tap_then_send: "Marca todas las que correspondan y envía",
    choose_one: "Elige una opción",
    pick_number: "Elige un número",
    pick_top: "Elige primero tu opción favorita",
    ranked_so_far: "Orden hasta ahora: {list} — elige el n.º {n}",
    card_where: "¿Dónde va «{card}»? ({n} de {total})",
    study_image: "imagen del estudio",
    upload_image: "Subir una imagen",
    uploading: "Subiendo…",
    upload_failed: "Error al subir: inténtalo de nuevo o pega un enlace.",
    didnt_catch: "No te he entendido; inténtalo de nuevo.",
    heard: "entendido «{raw}» → «{answer}»",
    speaking: "Hablando…",
    listening: "Escuchando… toca para terminar",
    listening_hands_free: "Escuchando: habla cuando quieras. Toca el micrófono para silenciarlo.",
    mic_hands_free: "Activar o desactivar el micrófono",
    transcribing: "Transcribiendo…",
    voice_unavailable: "Voz no disponible.",
    voice_hiccup: "Problema con la voz; inténtalo de nuevo.",
    mic_unavailable: "Micrófono no disponible: revisa los permisos.",
    recording_broken: "La grabación ha dejado de funcionar. La entrevista continúa y tus respuestas se siguen guardando como texto.",
    "rec.notice.title": "Grabación",
    "rec.notice.audio_required": "Se grabará tu audio y el del entrevistador. Es obligatorio para participar.",
    "rec.notice.video_required": "También se grabarán tu cámara y el vídeo del entrevistador. Es obligatorio para participar.",
    "rec.notice.audio_optional": "Grabar audio (el tuyo y el del entrevistador)",
    "rec.notice.video_optional": "Grabar vídeo (tu cámara y la del entrevistador)",
    "rec.notice.video_needs_audio": "El vídeo solo puede grabarse junto con el audio.",
    "rec.notice.continue": "Continuar",
    "rec.notice.decline": "No estoy de acuerdo",
    "rec.indicator.audio": "Esta entrevista se está grabando: tu audio y el del entrevistador se guardan para el equipo de investigación.",
    "rec.indicator.video": "Esta entrevista se está grabando: tu cámara y tu micrófono, y el vídeo y el audio del entrevistador, se guardan para el equipo de investigación.",
    "rec.indicator.paused": "Grabación en pausa: se reanudará cuando continúe la entrevista.",
    conversation_ended: "Esta conversación ha terminado.",
    message_failed: "No se pudo enviar el mensaje. Inténtalo de nuevo.",
    "reprompt.options": "Probemos de nuevo: elige una de las opciones de abajo.",
    "reprompt.multi_select": "Probemos de nuevo: elige entre las opciones de abajo.",
    "reprompt.scale": "Probemos de nuevo: elige un punto de la escala de abajo.",
    "reprompt.ranking": "Probemos de nuevo: ordena todos los elementos de abajo.",
    "reprompt.card_sort": "Probemos de nuevo: coloca cada elemento en uno de los grupos.",
    "done.title": "¡Listo! Muchas gracias.",
    "done.text": "Tus palabras llegan directamente al equipo que está construyendo esto.",
    "done.all_questions_asked": "Has respondido todas las preguntas. Tus palabras llegan directamente al equipo que está construyendo esto.",
    "done.consent_declined": "No pasa nada, gracias por pasarte.",
    "done.disqualified": "¡Gracias por tu interés! Este estudio en concreto no encajaba con tu perfil.",
    "done.global_turn_cap": "Eso es todo por ahora. Gracias por tu tiempo.",
    "closed.survey_full.title": "Este estudio está cerrado.",
    "closed.survey_full.text": "Ha alcanzado su número máximo de respuestas. ¡Gracias por tu interés!",
    "closed.start_failed.title": "No pudimos realizar esta entrevista.",
    "closed.start_failed.text": "Tuvimos un problema técnico. Vuelve a intentarlo en unos minutos o contacta con quien organiza el estudio si sigue ocurriendo.",
    "closed.quota_denied.title": "Este estudio no acepta respuestas.",
    "closed.quota_denied.text": "Ahora mismo no está aceptando nuevas respuestas. ¡Gracias por tu interés!",
    copy_link: "Copiar enlace",
    copied: "Copiado",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Datos de contacto",
    "contact.name": "Nombre",
    "contact.optional": "opcional",
    "contact.email": "Correo electrónico",
    "contact.phone": "Teléfono",
    "contact.email_placeholder": "tu@ejemplo.com",
    "contact.phone_placeholder": "+34 600 123 456",
    "contact.submit": "Compartir datos",
    "contact.decline": "Prefiero no compartirlos",
    "contact.privacy": "Se guardan por separado de tus respuestas.",
    "contact.shared": "Datos compartidos",
    "contact.declined": "Prefirió no compartirlos",
    "contact.err.invalid_name": "Ese nombre es demasiado largo.",
    "contact.err.invalid_email": "Introduce un correo electrónico válido.",
    "contact.err.invalid_phone": "Introduce un número de teléfono válido (de 7 a 15 dígitos).",
    "contact.err.missing_email": "Se necesita un correo electrónico para continuar.",
    "contact.err.missing_phone": "Se necesita un número de teléfono para continuar.",
    "contact.err.empty": "Introduce un correo electrónico o un número de teléfono.",
    "contact.err.required": "Este estudio necesita tus datos de contacto para participar.",
    "contact.err.failed": "No se pudieron guardar tus datos. Inténtalo de nuevo.",
    consent_suffix: "Responde Sí o No (o el número):",
  },

  fr: {
    interview: "Entretien",
    open_interview: "Ouvrir l'entretien",
    close_interview: "Fermer l'entretien",
    type_answer: "Tapez votre réponse…",
    your_answer: "Votre réponse",
    send: "Envoyer",
    hold_to_talk: "Maintenez pour parler",
    language: "Langue",
    language_error: "Impossible de changer de langue. Veuillez réessayer.",
    starting: "Démarrage de votre entretien…",
    start_failed: "Impossible de démarrer cet entretien. Veuillez réessayer.",
    progress: "Question {n} sur {total}",
    continue: "Continuer",
    start_interview: "Commencer l'entretien",
    listen: "Écouter",
    listen_stop: "Arrêter",
    listen_failed: "Impossible de lire l'accueil à voix haute — tout est écrit ci-dessus.",
    voice_intro: "Un intervieweur IA va vous poser à voix haute quelques questions. Répondez en parlant.",
    prefer_not: "Je préfère ne pas répondre",
    show_more: "Voir plus",
    n_selected: "{n} sélectionnées",
    tap_then_send: "Cochez tout ce qui s’applique, puis envoyez",
    choose_one: "Choisissez une option",
    pick_number: "Choisissez un nombre",
    pick_top: "Choisissez d'abord votre option préférée",
    ranked_so_far: "Classement actuel : {list} — choisissez le n° {n}",
    card_where: "Où va « {card} » ? ({n} sur {total})",
    study_image: "image de l'étude",
    upload_image: "Téléverser une image",
    uploading: "Téléversement…",
    upload_failed: "Échec du téléversement — réessayez ou collez un lien.",
    didnt_catch: "Je n'ai pas compris — réessayez.",
    heard: "entendu « {raw} » → « {answer} »",
    speaking: "Parole en cours…",
    listening: "Écoute… touchez pour terminer",
    listening_hands_free: "À l'écoute — parlez librement. Touchez le micro pour le couper.",
    mic_hands_free: "Activer ou couper le micro",
    transcribing: "Transcription…",
    voice_unavailable: "Voix indisponible.",
    voice_hiccup: "Problème de voix — réessayez.",
    mic_unavailable: "Micro indisponible — vérifiez les autorisations.",
    recording_broken: "L'enregistrement ne fonctionne plus — l'entretien continue et vos réponses sont toujours enregistrées sous forme de texte.",
    "rec.notice.title": "Enregistrement",
    "rec.notice.audio_required": "Votre audio et celui de l'intervieweur seront enregistrés. C'est obligatoire pour participer.",
    "rec.notice.video_required": "Votre caméra et la vidéo de l'intervieweur seront également enregistrées. C'est obligatoire pour participer.",
    "rec.notice.audio_optional": "Enregistrer l'audio (le vôtre et celui de l'intervieweur)",
    "rec.notice.video_optional": "Enregistrer la vidéo (votre caméra et celle de l'intervieweur)",
    "rec.notice.video_needs_audio": "La vidéo ne peut être enregistrée qu'avec l'audio.",
    "rec.notice.continue": "Continuer",
    "rec.notice.decline": "Je ne suis pas d'accord",
    "rec.indicator.audio": "Cet entretien est enregistré — votre audio et celui de l'intervieweur sont conservés pour l'équipe de recherche.",
    "rec.indicator.video": "Cet entretien est enregistré — votre caméra et votre micro, ainsi que la vidéo et l'audio de l'intervieweur, sont conservés pour l'équipe de recherche.",
    "rec.indicator.paused": "Enregistrement en pause — il reprendra quand l'entretien continuera.",
    conversation_ended: "Cette conversation est terminée.",
    message_failed: "Échec de l'envoi — veuillez réessayer.",
    "reprompt.options": "Réessayons — choisissez l'une des options ci-dessous.",
    "reprompt.multi_select": "Réessayons — choisissez parmi les options ci-dessous.",
    "reprompt.scale": "Réessayons — choisissez un point sur l'échelle ci-dessous.",
    "reprompt.ranking": "Réessayons — classez tous les éléments ci-dessous.",
    "reprompt.card_sort": "Réessayons — placez chaque élément dans l'un des groupes.",
    "done.title": "C'est terminé — merci.",
    "done.text": "Vos mots vont directement à l'équipe qui construit ce produit.",
    "done.all_questions_asked": "Vous avez répondu à toutes les questions. Vos mots vont directement à l'équipe qui construit ce produit.",
    "done.consent_declined": "Pas de problème — merci de votre visite.",
    "done.disqualified": "Merci de votre intérêt ! Cette étude ne correspondait pas à votre profil.",
    "done.global_turn_cap": "C'est tout pour le moment — merci pour votre temps.",
    "closed.survey_full.title": "Cette étude est close.",
    "closed.survey_full.text": "Elle a atteint son nombre maximal de réponses. Merci de votre intérêt !",
    "closed.start_failed.title": "Nous n'avons pas pu mener cet entretien.",
    "closed.start_failed.text": "Nous avons rencontré un problème technique. Réessayez dans quelques minutes, ou contactez l'organisateur de l'étude si le problème persiste.",
    "closed.quota_denied.title": "Cette étude n'accepte pas de réponses.",
    "closed.quota_denied.text": "Elle n'accepte pas de nouvelles réponses pour le moment. Merci de votre intérêt !",
    copy_link: "Copier le lien",
    copied: "Copié",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Coordonnées",
    "contact.name": "Nom",
    "contact.optional": "facultatif",
    "contact.email": "E-mail",
    "contact.phone": "Téléphone",
    "contact.email_placeholder": "vous@exemple.fr",
    "contact.phone_placeholder": "+33 6 12 34 56 78",
    "contact.submit": "Partager",
    "contact.decline": "Je préfère ne pas les partager",
    "contact.privacy": "Conservées séparément de vos réponses.",
    "contact.shared": "Coordonnées partagées",
    "contact.declined": "Préfère ne pas les partager",
    "contact.err.invalid_name": "Ce nom est trop long.",
    "contact.err.invalid_email": "Saisissez une adresse e-mail valide.",
    "contact.err.invalid_phone": "Saisissez un numéro de téléphone valide (7 à 15 chiffres).",
    "contact.err.missing_email": "Une adresse e-mail est nécessaire pour continuer.",
    "contact.err.missing_phone": "Un numéro de téléphone est nécessaire pour continuer.",
    "contact.err.empty": "Saisissez une adresse e-mail ou un numéro de téléphone.",
    "contact.err.required": "Cette étude a besoin de vos coordonnées pour que vous puissiez participer.",
    "contact.err.failed": "Vos coordonnées n’ont pas pu être enregistrées. Veuillez réessayer.",
    consent_suffix: "Veuillez répondre par Oui ou Non (ou le numéro) :",
  },

  de: {
    interview: "Interview",
    open_interview: "Interview öffnen",
    close_interview: "Interview schließen",
    type_answer: "Antwort eingeben…",
    your_answer: "Ihre Antwort",
    send: "Senden",
    hold_to_talk: "Zum Sprechen gedrückt halten",
    language: "Sprache",
    language_error: "Die Sprache konnte nicht geändert werden. Bitte versuchen Sie es erneut.",
    starting: "Ihr Interview wird gestartet…",
    start_failed: "Dieses Interview konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
    progress: "Frage {n} von {total}",
    continue: "Weiter",
    start_interview: "Interview starten",
    listen: "Anhören",
    listen_stop: "Stopp",
    listen_failed: "Die Begrüßung konnte nicht vorgelesen werden – sie steht oben vollständig.",
    voice_intro: "Ein KI-Interviewer stellt Ihnen laut ein paar kurze Fragen. Antworten Sie einfach mündlich.",
    prefer_not: "Keine Angabe",
    show_more: "Mehr anzeigen",
    n_selected: "{n} ausgewählt",
    tap_then_send: "Alles Zutreffende antippen, dann senden",
    choose_one: "Eine Option wählen",
    pick_number: "Eine Zahl wählen",
    pick_top: "Wählen Sie zuerst Ihre erste Wahl",
    ranked_so_far: "Bisherige Reihenfolge: {list} — wählen Sie Platz {n}",
    card_where: "Wohin gehört „{card}“? ({n} von {total})",
    study_image: "Studienbild",
    upload_image: "Bild hochladen",
    uploading: "Wird hochgeladen…",
    upload_failed: "Hochladen fehlgeschlagen — erneut versuchen oder einen Link einfügen.",
    didnt_catch: "Das habe ich nicht verstanden — bitte noch einmal.",
    heard: "verstanden „{raw}“ → „{answer}“",
    speaking: "Spricht…",
    listening: "Hört zu… zum Beenden tippen",
    listening_hands_free: "Hört zu – einfach sprechen. Zum Stummschalten auf das Mikrofon tippen.",
    mic_hands_free: "Mikrofon ein- oder ausschalten",
    transcribing: "Wird transkribiert…",
    voice_unavailable: "Sprache nicht verfügbar.",
    voice_hiccup: "Sprachproblem — bitte erneut versuchen.",
    mic_unavailable: "Mikrofon nicht verfügbar — bitte Berechtigungen prüfen.",
    recording_broken: "Die Aufnahme funktioniert nicht mehr — das Interview läuft weiter und Ihre Antworten werden weiterhin als Text gespeichert.",
    "rec.notice.title": "Aufzeichnung",
    "rec.notice.audio_required": "Ihr Audio und das des Interviewers werden aufgezeichnet. Das ist für die Teilnahme erforderlich.",
    "rec.notice.video_required": "Auch Ihre Kamera und das Video des Interviewers werden aufgezeichnet. Das ist für die Teilnahme erforderlich.",
    "rec.notice.audio_optional": "Audio aufzeichnen (Ihres und das des Interviewers)",
    "rec.notice.video_optional": "Video aufzeichnen (Ihre Kamera und die des Interviewers)",
    "rec.notice.video_needs_audio": "Video kann nur zusammen mit Audio aufgezeichnet werden.",
    "rec.notice.continue": "Weiter",
    "rec.notice.decline": "Ich stimme nicht zu",
    "rec.indicator.audio": "Dieses Interview wird aufgezeichnet — Ihr Audio und das des Interviewers werden für das Forschungsteam gespeichert.",
    "rec.indicator.video": "Dieses Interview wird aufgezeichnet — Ihre Kamera und Ihr Mikrofon sowie Video und Audio des Interviewers werden für das Forschungsteam gespeichert.",
    "rec.indicator.paused": "Aufzeichnung pausiert — sie wird fortgesetzt, sobald das Interview weitergeht.",
    conversation_ended: "Dieses Gespräch ist beendet.",
    message_failed: "Nachricht konnte nicht gesendet werden — bitte erneut versuchen.",
    "reprompt.options": "Versuchen wir es noch einmal — wählen Sie unten eine der Optionen.",
    "reprompt.multi_select": "Versuchen wir es noch einmal — wählen Sie aus den Optionen unten.",
    "reprompt.scale": "Versuchen wir es noch einmal — wählen Sie einen Punkt auf der Skala unten.",
    "reprompt.ranking": "Versuchen wir es noch einmal — bringen Sie unten alle Elemente in eine Reihenfolge.",
    "reprompt.card_sort": "Versuchen wir es noch einmal — ordnen Sie jedes Element einer der Gruppen zu.",
    "done.title": "Geschafft — vielen Dank.",
    "done.text": "Ihre Antworten gehen direkt an das Team, das dies entwickelt.",
    "done.all_questions_asked": "Sie haben alle Fragen beantwortet. Ihre Antworten gehen direkt an das Team, das dies entwickelt.",
    "done.consent_declined": "Kein Problem — danke, dass Sie vorbeigeschaut haben.",
    "done.disqualified": "Danke für Ihr Interesse! Diese Studie passt leider nicht zu Ihnen.",
    "done.global_turn_cap": "Das war's für den Moment — vielen Dank für Ihre Zeit.",
    "closed.survey_full.title": "Diese Studie ist geschlossen.",
    "closed.survey_full.text": "Die maximale Anzahl an Antworten ist erreicht. Vielen Dank für Ihr Interesse!",
    "closed.start_failed.title": "Wir konnten dieses Interview nicht durchführen.",
    "closed.start_failed.text": "Bei uns ist ein technisches Problem aufgetreten. Bitte versuchen Sie es in ein paar Minuten erneut oder wenden Sie sich an die Studienleitung, falls es weiter auftritt.",
    "closed.quota_denied.title": "Diese Studie nimmt keine Antworten an.",
    "closed.quota_denied.text": "Derzeit werden keine neuen Antworten angenommen. Vielen Dank für Ihr Interesse!",
    copy_link: "Link kopieren",
    copied: "Kopiert",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Kontaktdaten",
    "contact.name": "Name",
    "contact.optional": "optional",
    "contact.email": "E-Mail",
    "contact.phone": "Telefon",
    "contact.email_placeholder": "sie@beispiel.de",
    "contact.phone_placeholder": "+49 151 23456789",
    "contact.submit": "Angaben teilen",
    "contact.decline": "Lieber nicht angeben",
    "contact.privacy": "Getrennt von Ihren Antworten gespeichert.",
    "contact.shared": "Angaben geteilt",
    "contact.declined": "Lieber nicht angegeben",
    "contact.err.invalid_name": "Dieser Name ist zu lang.",
    "contact.err.invalid_email": "Geben Sie eine gültige E-Mail-Adresse ein.",
    "contact.err.invalid_phone": "Geben Sie eine gültige Telefonnummer ein (7–15 Ziffern).",
    "contact.err.missing_email": "Zum Fortfahren wird eine E-Mail-Adresse benötigt.",
    "contact.err.missing_phone": "Zum Fortfahren wird eine Telefonnummer benötigt.",
    "contact.err.empty": "Geben Sie eine E-Mail-Adresse oder Telefonnummer ein.",
    "contact.err.required": "Für die Teilnahme an dieser Studie werden Kontaktdaten benötigt.",
    "contact.err.failed": "Ihre Angaben konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    consent_suffix: "Bitte antworten Sie mit Ja oder Nein (oder der Nummer):",
  },

  it: {
    interview: "Intervista",
    open_interview: "Apri intervista",
    close_interview: "Chiudi intervista",
    type_answer: "Scrivi la tua risposta…",
    your_answer: "La tua risposta",
    send: "Invia",
    hold_to_talk: "Tieni premuto per parlare",
    language: "Lingua",
    language_error: "Impossibile cambiare lingua. Riprova.",
    starting: "Avvio della tua intervista…",
    start_failed: "Impossibile avviare questa intervista. Riprova.",
    progress: "Domanda {n} di {total}",
    continue: "Continua",
    start_interview: "Inizia l'intervista",
    listen: "Ascolta",
    listen_stop: "Ferma",
    listen_failed: "Non è stato possibile leggere il benvenuto ad alta voce: è tutto scritto qui sopra.",
    voice_intro: "Un intervistatore IA ti farà ad alta voce alcune brevi domande. Rispondi parlando.",
    prefer_not: "Preferisco non rispondere",
    show_more: "Mostra altro",
    n_selected: "{n} selezionate",
    tap_then_send: "Tocca tutte le opzioni pertinenti, poi invia",
    choose_one: "Scegli un'opzione",
    pick_number: "Scegli un numero",
    pick_top: "Scegli prima la tua preferita",
    ranked_so_far: "Ordine finora: {list} — scegli il n. {n}",
    card_where: "Dove va «{card}»? ({n} di {total})",
    study_image: "immagine dello studio",
    upload_image: "Carica un'immagine",
    uploading: "Caricamento…",
    upload_failed: "Caricamento non riuscito: riprova o incolla un link.",
    didnt_catch: "Non ho capito: riprova.",
    heard: "sentito «{raw}» → «{answer}»",
    speaking: "Sta parlando…",
    listening: "In ascolto… tocca per terminare",
    listening_hands_free: "In ascolto: parla pure. Tocca il microfono per disattivarlo.",
    mic_hands_free: "Attiva o disattiva il microfono",
    transcribing: "Trascrizione…",
    voice_unavailable: "Voce non disponibile.",
    voice_hiccup: "Problema con la voce: riprova.",
    mic_unavailable: "Microfono non disponibile: controlla le autorizzazioni.",
    recording_broken: "La registrazione ha smesso di funzionare: l'intervista continua e le tue risposte vengono comunque salvate come testo.",
    "rec.notice.title": "Registrazione",
    "rec.notice.audio_required": "Il tuo audio e quello dell'intervistatore verranno registrati. È necessario per partecipare.",
    "rec.notice.video_required": "Verranno registrati anche la tua videocamera e il video dell'intervistatore. È necessario per partecipare.",
    "rec.notice.audio_optional": "Registra l'audio (il tuo e quello dell'intervistatore)",
    "rec.notice.video_optional": "Registra il video (la tua videocamera e quella dell'intervistatore)",
    "rec.notice.video_needs_audio": "Il video può essere registrato solo insieme all'audio.",
    "rec.notice.continue": "Continua",
    "rec.notice.decline": "Non sono d'accordo",
    "rec.indicator.audio": "Questa intervista viene registrata: il tuo audio e quello dell'intervistatore vengono salvati per il team di ricerca.",
    "rec.indicator.video": "Questa intervista viene registrata: la tua videocamera e il tuo microfono, insieme al video e all'audio dell'intervistatore, vengono salvati per il team di ricerca.",
    "rec.indicator.paused": "Registrazione in pausa: riprenderà quando l'intervista continua.",
    conversation_ended: "Questa conversazione è terminata.",
    message_failed: "Invio non riuscito: riprova.",
    "reprompt.options": "Riproviamo: scegli una delle opzioni qui sotto.",
    "reprompt.multi_select": "Riproviamo: scegli tra le opzioni qui sotto.",
    "reprompt.scale": "Riproviamo: scegli un punto della scala qui sotto.",
    "reprompt.ranking": "Riproviamo: metti in ordine tutti gli elementi qui sotto.",
    "reprompt.card_sort": "Riproviamo: inserisci ogni elemento in uno dei gruppi.",
    "done.title": "Fatto: grazie mille.",
    "done.text": "Le tue parole arrivano direttamente al team che sta costruendo tutto questo.",
    "done.all_questions_asked": "Hai risposto a tutte le domande. Le tue parole arrivano direttamente al team che sta costruendo tutto questo.",
    "done.consent_declined": "Nessun problema, grazie per essere passato.",
    "done.disqualified": "Grazie per l'interesse! Questo studio non era adatto al tuo profilo.",
    "done.global_turn_cap": "Per ora è tutto: grazie per il tuo tempo.",
    "closed.survey_full.title": "Questo studio è chiuso.",
    "closed.survey_full.text": "Ha raggiunto il numero massimo di risposte. Grazie per l'interesse!",
    "closed.start_failed.title": "Non siamo riusciti a svolgere questa intervista.",
    "closed.start_failed.text": "Abbiamo avuto un problema tecnico. Riprova tra qualche minuto o contatta chi organizza lo studio se il problema persiste.",
    "closed.quota_denied.title": "Questo studio non accetta risposte.",
    "closed.quota_denied.text": "Al momento non accetta nuove risposte. Grazie per l'interesse!",
    copy_link: "Copia link",
    copied: "Copiato",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Recapiti",
    "contact.name": "Nome",
    "contact.optional": "facoltativo",
    "contact.email": "Email",
    "contact.phone": "Telefono",
    "contact.email_placeholder": "tu@esempio.it",
    "contact.phone_placeholder": "+39 312 345 6789",
    "contact.submit": "Condividi",
    "contact.decline": "Preferisco non condividerli",
    "contact.privacy": "Conservati separatamente dalle tue risposte.",
    "contact.shared": "Recapiti condivisi",
    "contact.declined": "Ha preferito non condividerli",
    "contact.err.invalid_name": "Questo nome è troppo lungo.",
    "contact.err.invalid_email": "Inserisci un indirizzo email valido.",
    "contact.err.invalid_phone": "Inserisci un numero di telefono valido (da 7 a 15 cifre).",
    "contact.err.missing_email": "Per continuare serve un indirizzo email.",
    "contact.err.missing_phone": "Per continuare serve un numero di telefono.",
    "contact.err.empty": "Inserisci un indirizzo email o un numero di telefono.",
    "contact.err.required": "Per partecipare a questo studio servono i tuoi recapiti.",
    "contact.err.failed": "Non è stato possibile salvare i tuoi recapiti. Riprova.",
    consent_suffix: "Rispondi Sì o No (o con il numero):",
  },

  pt: {
    interview: "Entrevista",
    open_interview: "Abrir entrevista",
    close_interview: "Fechar entrevista",
    type_answer: "Digite sua resposta…",
    your_answer: "Sua resposta",
    send: "Enviar",
    hold_to_talk: "Segure para falar",
    language: "Idioma",
    language_error: "Não foi possível mudar o idioma. Tente novamente.",
    starting: "Iniciando sua entrevista…",
    start_failed: "Não foi possível iniciar esta entrevista. Tente novamente.",
    progress: "Pergunta {n} de {total}",
    continue: "Continuar",
    start_interview: "Começar a entrevista",
    listen: "Ouvir",
    listen_stop: "Parar",
    listen_failed: "Não foi possível ler as boas-vindas em voz alta — está tudo escrito acima.",
    voice_intro: "Um entrevistador de IA vai fazer algumas perguntas curtas em voz alta. Responda falando.",
    prefer_not: "Prefiro não responder",
    show_more: "Ver mais",
    n_selected: "{n} selecionadas",
    tap_then_send: "Toque em todas as que se aplicam e envie",
    choose_one: "Escolha uma opção",
    pick_number: "Escolha um número",
    pick_top: "Escolha primeiro sua opção favorita",
    ranked_so_far: "Ordem até agora: {list} — escolha o nº {n}",
    card_where: "Onde fica “{card}”? ({n} de {total})",
    study_image: "imagem do estudo",
    upload_image: "Enviar uma imagem",
    uploading: "Enviando…",
    upload_failed: "Falha no envio — tente novamente ou cole um link.",
    didnt_catch: "Não entendi — tente novamente.",
    heard: "entendido “{raw}” → “{answer}”",
    speaking: "Falando…",
    listening: "Ouvindo… toque para terminar",
    listening_hands_free: "Ouvindo — é só falar. Toque no microfone para silenciar.",
    mic_hands_free: "Ligar ou desligar o microfone",
    transcribing: "Transcrevendo…",
    voice_unavailable: "Voz indisponível.",
    voice_hiccup: "Problema na voz — tente novamente.",
    mic_unavailable: "Microfone indisponível — verifique as permissões.",
    recording_broken: "A gravação parou de funcionar — a entrevista continua e suas respostas continuam sendo salvas como texto.",
    "rec.notice.title": "Gravação",
    "rec.notice.audio_required": "O seu áudio e o do entrevistador serão gravados. Isso é obrigatório para participar.",
    "rec.notice.video_required": "A sua câmera e o vídeo do entrevistador também serão gravados. Isso é obrigatório para participar.",
    "rec.notice.audio_optional": "Gravar áudio (o seu e o do entrevistador)",
    "rec.notice.video_optional": "Gravar vídeo (a sua câmera e a do entrevistador)",
    "rec.notice.video_needs_audio": "O vídeo só pode ser gravado junto com o áudio.",
    "rec.notice.continue": "Continuar",
    "rec.notice.decline": "Não concordo",
    "rec.indicator.audio": "Esta entrevista está sendo gravada — o seu áudio e o do entrevistador são guardados para a equipe de pesquisa.",
    "rec.indicator.video": "Esta entrevista está sendo gravada — a sua câmera e o seu microfone, e o vídeo e o áudio do entrevistador, são guardados para a equipe de pesquisa.",
    "rec.indicator.paused": "Gravação pausada — ela será retomada quando a entrevista continuar.",
    conversation_ended: "Esta conversa terminou.",
    message_failed: "Falha ao enviar a mensagem — tente novamente.",
    "reprompt.options": "Vamos tentar de novo — escolha uma das opções abaixo.",
    "reprompt.multi_select": "Vamos tentar de novo — escolha entre as opções abaixo.",
    "reprompt.scale": "Vamos tentar de novo — escolha um ponto na escala abaixo.",
    "reprompt.ranking": "Vamos tentar de novo — coloque todos os itens abaixo em ordem.",
    "reprompt.card_sort": "Vamos tentar de novo — coloque cada item em um dos grupos.",
    "done.title": "Pronto — muito obrigado.",
    "done.text": "Suas palavras vão direto para a equipe que está construindo isto.",
    "done.all_questions_asked": "Você respondeu a todas as perguntas. Suas palavras vão direto para a equipe que está construindo isto.",
    "done.consent_declined": "Sem problema — obrigado pela visita.",
    "done.disqualified": "Obrigado pelo interesse! Este estudo não era adequado para o seu perfil.",
    "done.global_turn_cap": "Por enquanto é só — obrigado pelo seu tempo.",
    "closed.survey_full.title": "Este estudo está encerrado.",
    "closed.survey_full.text": "Ele atingiu o número máximo de respostas. Obrigado pelo interesse!",
    "closed.start_failed.title": "Não conseguimos realizar esta entrevista.",
    "closed.start_failed.text": "Tivemos um problema técnico. Tente novamente em alguns minutos ou entre em contato com o organizador do estudo se continuar acontecendo.",
    "closed.quota_denied.title": "Este estudo não está aceitando respostas.",
    "closed.quota_denied.text": "No momento, ele não está aceitando novas respostas. Obrigado pelo interesse!",
    copy_link: "Copiar link",
    copied: "Copiado",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Dados de contato",
    "contact.name": "Nome",
    "contact.optional": "opcional",
    "contact.email": "E-mail",
    "contact.phone": "Telefone",
    "contact.email_placeholder": "voce@exemplo.com",
    "contact.phone_placeholder": "+55 11 91234 5678",
    "contact.submit": "Compartilhar",
    "contact.decline": "Prefiro não compartilhar",
    "contact.privacy": "Guardados separadamente das suas respostas.",
    "contact.shared": "Dados compartilhados",
    "contact.declined": "Preferiu não compartilhar",
    "contact.err.invalid_name": "Esse nome é longo demais.",
    "contact.err.invalid_email": "Digite um endereço de e-mail válido.",
    "contact.err.invalid_phone": "Digite um número de telefone válido (7 a 15 dígitos).",
    "contact.err.missing_email": "É preciso um endereço de e-mail para continuar.",
    "contact.err.missing_phone": "É preciso um número de telefone para continuar.",
    "contact.err.empty": "Digite um endereço de e-mail ou número de telefone.",
    "contact.err.required": "Este estudo precisa dos seus dados de contato para você participar.",
    "contact.err.failed": "Não foi possível salvar seus dados. Tente novamente.",
    consent_suffix: "Responda Sim ou Não (ou o número):",
  },

  ru: {
    interview: "Интервью",
    open_interview: "Открыть интервью",
    close_interview: "Закрыть интервью",
    type_answer: "Введите ответ…",
    your_answer: "Ваш ответ",
    send: "Отправить",
    hold_to_talk: "Удерживайте, чтобы говорить",
    language: "Язык",
    language_error: "Не удалось сменить язык. Попробуйте ещё раз.",
    starting: "Запускаем ваше интервью…",
    start_failed: "Не удалось начать интервью. Попробуйте ещё раз.",
    progress: "Вопрос {n} из {total}",
    continue: "Продолжить",
    start_interview: "Начать интервью",
    listen: "Прослушать",
    listen_stop: "Стоп",
    listen_failed: "Не удалось воспроизвести приветствие — оно полностью написано выше.",
    voice_intro: "ИИ-интервьюер задаст вам вслух несколько коротких вопросов. Отвечайте голосом.",
    prefer_not: "Предпочитаю не отвечать",
    show_more: "Показать ещё",
    n_selected: "Выбрано: {n}",
    tap_then_send: "Отметьте все подходящие и отправьте",
    choose_one: "Выберите вариант",
    pick_number: "Выберите число",
    pick_top: "Сначала выберите самый важный вариант",
    ranked_so_far: "Порядок сейчас: {list} — выберите № {n}",
    card_where: "Куда относится «{card}»? ({n} из {total})",
    study_image: "изображение исследования",
    upload_image: "Загрузить изображение",
    uploading: "Загрузка…",
    upload_failed: "Не удалось загрузить — попробуйте снова или вставьте ссылку.",
    didnt_catch: "Не удалось разобрать — попробуйте ещё раз.",
    heard: "распознано «{raw}» → «{answer}»",
    speaking: "Говорит…",
    listening: "Слушаю… нажмите, чтобы закончить",
    listening_hands_free: "Слушаю — просто говорите. Нажмите на микрофон, чтобы выключить его.",
    mic_hands_free: "Включить или выключить микрофон",
    transcribing: "Расшифровка…",
    voice_unavailable: "Голосовой режим недоступен.",
    voice_hiccup: "Сбой голоса — попробуйте ещё раз.",
    mic_unavailable: "Микрофон недоступен — проверьте разрешения.",
    recording_broken: "Запись перестала работать — интервью продолжается, и ваши ответы по-прежнему сохраняются в виде текста.",
    "rec.notice.title": "Запись",
    "rec.notice.audio_required": "Ваш звук и звук интервьюера будут записаны. Без этого участие невозможно.",
    "rec.notice.video_required": "Изображение с вашей камеры и видео интервьюера тоже будут записаны. Без этого участие невозможно.",
    "rec.notice.audio_optional": "Записывать звук (ваш и интервьюера)",
    "rec.notice.video_optional": "Записывать видео (с вашей камеры и интервьюера)",
    "rec.notice.video_needs_audio": "Видео можно записывать только вместе со звуком.",
    "rec.notice.continue": "Продолжить",
    "rec.notice.decline": "Я не даю согласия",
    "rec.indicator.audio": "Это интервью записывается — ваш звук и звук интервьюера сохраняются для исследовательской группы.",
    "rec.indicator.video": "Это интервью записывается — ваши камера и микрофон, а также видео и звук интервьюера сохраняются для исследовательской группы.",
    "rec.indicator.paused": "Запись приостановлена — она возобновится, когда интервью продолжится.",
    conversation_ended: "Этот разговор завершён.",
    message_failed: "Не удалось отправить сообщение — попробуйте ещё раз.",
    "reprompt.options": "Давайте попробуем ещё раз — выберите один из вариантов ниже.",
    "reprompt.multi_select": "Давайте попробуем ещё раз — выберите из вариантов ниже.",
    "reprompt.scale": "Давайте попробуем ещё раз — выберите точку на шкале ниже.",
    "reprompt.ranking": "Давайте попробуем ещё раз — расставьте все пункты ниже по порядку.",
    "reprompt.card_sort": "Давайте попробуем ещё раз — распределите каждый пункт по группам.",
    "done.title": "Готово — спасибо!",
    "done.text": "Ваши ответы попадут прямо к команде, которая это создаёт.",
    "done.all_questions_asked": "Вы ответили на все вопросы. Ваши ответы попадут прямо к команде, которая это создаёт.",
    "done.consent_declined": "Ничего страшного — спасибо, что заглянули.",
    "done.disqualified": "Спасибо за интерес! Это исследование вам, к сожалению, не подошло.",
    "done.global_turn_cap": "Пока на этом всё — спасибо за ваше время.",
    "closed.survey_full.title": "Это исследование закрыто.",
    "closed.survey_full.text": "Набрано максимальное количество ответов. Спасибо за интерес!",
    "closed.start_failed.title": "Не удалось провести это интервью.",
    "closed.start_failed.text": "У нас возникла техническая проблема. Попробуйте ещё раз через несколько минут или свяжитесь с организатором исследования, если ошибка повторится.",
    "closed.quota_denied.title": "Это исследование не принимает ответы.",
    "closed.quota_denied.text": "Сейчас новые ответы не принимаются. Спасибо за интерес!",
    copy_link: "Скопировать ссылку",
    copied: "Скопировано",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "Контактные данные",
    "contact.name": "Имя",
    "contact.optional": "необязательно",
    "contact.email": "Эл. почта",
    "contact.phone": "Телефон",
    "contact.email_placeholder": "vy@primer.ru",
    "contact.phone_placeholder": "+7 912 345-67-89",
    "contact.submit": "Отправить",
    "contact.decline": "Предпочитаю не сообщать",
    "contact.privacy": "Хранятся отдельно от ваших ответов.",
    "contact.shared": "Данные отправлены",
    "contact.declined": "Предпочёл(а) не сообщать",
    "contact.err.invalid_name": "Слишком длинное имя.",
    "contact.err.invalid_email": "Введите действительный адрес электронной почты.",
    "contact.err.invalid_phone": "Введите действительный номер телефона (7–15 цифр).",
    "contact.err.missing_email": "Чтобы продолжить, нужен адрес электронной почты.",
    "contact.err.missing_phone": "Чтобы продолжить, нужен номер телефона.",
    "contact.err.empty": "Введите адрес электронной почты или номер телефона.",
    "contact.err.required": "Для участия в этом исследовании нужны контактные данные.",
    "contact.err.failed": "Не удалось сохранить ваши данные. Попробуйте ещё раз.",
    consent_suffix: "Пожалуйста, ответьте «Да» или «Нет» (или номером):",
  },

  ja: {
    interview: "インタビュー",
    open_interview: "インタビューを開く",
    close_interview: "インタビューを閉じる",
    type_answer: "回答を入力…",
    your_answer: "あなたの回答",
    send: "送信",
    hold_to_talk: "押している間話す",
    language: "言語",
    language_error: "言語を変更できませんでした。もう一度お試しください。",
    starting: "インタビューを開始しています…",
    start_failed: "インタビューを開始できませんでした。もう一度お試しください。",
    progress: "質問 {n} / {total}",
    continue: "続ける",
    start_interview: "インタビューを始める",
    listen: "聞く",
    listen_stop: "停止",
    listen_failed: "ようこそメッセージを読み上げられませんでした。内容は上に書かれています。",
    voice_intro: "AIインタビュアーがいくつかの短い質問を音声でお尋ねします。声でお答えください。",
    prefer_not: "回答しない",
    show_more: "もっと見る",
    n_selected: "{n}件選択中",
    tap_then_send: "当てはまるものをすべて選んで送信",
    choose_one: "1つ選んでください",
    pick_number: "数字を選んでください",
    pick_top: "まず一番のものを選んでください",
    ranked_so_far: "現在の順位：{list} — {n}位を選んでください",
    card_where: "「{card}」はどこに入りますか？（{total}件中{n}件目）",
    study_image: "調査の画像",
    upload_image: "画像をアップロード",
    uploading: "アップロード中…",
    upload_failed: "アップロードに失敗しました。もう一度試すか、リンクを貼り付けてください。",
    didnt_catch: "聞き取れませんでした。もう一度お試しください。",
    heard: "認識「{raw}」→「{answer}」",
    speaking: "話しています…",
    listening: "聞いています… タップで終了",
    listening_hands_free: "聞いています。そのまま話してください。マイクをタップするとミュートします。",
    mic_hands_free: "マイクのオン／オフ",
    transcribing: "文字起こし中…",
    voice_unavailable: "音声は利用できません。",
    voice_hiccup: "音声に問題が発生しました。もう一度お試しください。",
    mic_unavailable: "マイクを使用できません。権限を確認してください。",
    recording_broken: "録音が停止しました。インタビューは続行され、回答は引き続きテキストとして保存されます。",
    "rec.notice.title": "録音・録画",
    "rec.notice.audio_required": "あなたとインタビュアーの音声が録音されます。参加にはこれが必要です。",
    "rec.notice.video_required": "あなたのカメラ映像とインタビュアーの映像も録画されます。参加にはこれが必要です。",
    "rec.notice.audio_optional": "音声を録音する（あなたとインタビュアーの音声）",
    "rec.notice.video_optional": "映像を録画する（あなたのカメラとインタビュアーの映像）",
    "rec.notice.video_needs_audio": "映像は音声と一緒にのみ録画できます。",
    "rec.notice.continue": "続ける",
    "rec.notice.decline": "同意しません",
    "rec.indicator.audio": "このインタビューは録音されています。あなたとインタビュアーの音声は研究チームのために保存されます。",
    "rec.indicator.video": "このインタビューは録画されています。あなたのカメラとマイク、インタビュアーの映像と音声は研究チームのために保存されます。",
    "rec.indicator.paused": "録音・録画は一時停止中です。インタビューが再開すると再開されます。",
    conversation_ended: "この会話は終了しました。",
    message_failed: "メッセージを送信できませんでした。もう一度お試しください。",
    "reprompt.options": "もう一度お願いします。下の選択肢から1つ選んでください。",
    "reprompt.multi_select": "もう一度お願いします。下の選択肢から選んでください。",
    "reprompt.scale": "もう一度お願いします。下のスケールから1つ選んでください。",
    "reprompt.ranking": "もう一度お願いします。下のすべての項目を並べ替えてください。",
    "reprompt.card_sort": "もう一度お願いします。すべての項目をいずれかのグループに入れてください。",
    "done.title": "以上です。ありがとうございました。",
    "done.text": "いただいた言葉は、これを作っているチームに直接届きます。",
    "done.all_questions_asked": "すべての質問にお答えいただきました。いただいた言葉は、これを作っているチームに直接届きます。",
    "done.consent_declined": "承知しました。お立ち寄りいただきありがとうございました。",
    "done.disqualified": "ご関心をお寄せいただきありがとうございます。今回の調査は対象外となりました。",
    "done.global_turn_cap": "今回はここまでです。お時間をいただきありがとうございました。",
    "closed.survey_full.title": "この調査は終了しました。",
    "closed.survey_full.text": "回答数が上限に達しました。ご関心をお寄せいただきありがとうございます。",
    "closed.start_failed.title": "インタビューを実施できませんでした。",
    "closed.start_failed.text": "技術的な問題が発生しました。数分後にもう一度お試しいただくか、問題が続く場合は調査の主催者にお問い合わせください。",
    "closed.quota_denied.title": "この調査は回答を受け付けていません。",
    "closed.quota_denied.text": "現在、新しい回答は受け付けていません。ご関心をお寄せいただきありがとうございます。",
    copy_link: "リンクをコピー",
    copied: "コピーしました",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "連絡先",
    "contact.name": "お名前",
    "contact.optional": "任意",
    "contact.email": "メールアドレス",
    "contact.phone": "電話番号",
    "contact.email_placeholder": "you@example.jp",
    "contact.phone_placeholder": "090-1234-5678",
    "contact.submit": "送信する",
    "contact.decline": "共有しない",
    "contact.privacy": "回答とは別に保管されます。",
    "contact.shared": "連絡先を共有しました",
    "contact.declined": "共有しないを選択",
    "contact.err.invalid_name": "お名前が長すぎます。",
    "contact.err.invalid_email": "有効なメールアドレスを入力してください。",
    "contact.err.invalid_phone": "有効な電話番号を入力してください（7〜15桁）。",
    "contact.err.missing_email": "続けるにはメールアドレスが必要です。",
    "contact.err.missing_phone": "続けるには電話番号が必要です。",
    "contact.err.empty": "メールアドレスまたは電話番号を入力してください。",
    "contact.err.required": "この調査に参加するには連絡先が必要です。",
    "contact.err.failed": "連絡先を保存できませんでした。もう一度お試しください。",
    consent_suffix: "「はい」または「いいえ」（または番号）でお答えください：",
  },

  ko: {
    interview: "인터뷰",
    open_interview: "인터뷰 열기",
    close_interview: "인터뷰 닫기",
    type_answer: "답변을 입력하세요…",
    your_answer: "내 답변",
    send: "보내기",
    hold_to_talk: "누르고 말하기",
    language: "언어",
    language_error: "언어를 변경할 수 없습니다. 다시 시도해 주세요.",
    starting: "인터뷰를 시작하는 중…",
    start_failed: "인터뷰를 시작할 수 없습니다. 다시 시도해 주세요.",
    progress: "질문 {n}/{total}",
    continue: "계속",
    start_interview: "인터뷰 시작",
    listen: "듣기",
    listen_stop: "정지",
    listen_failed: "환영 메시지를 읽어 드리지 못했습니다. 내용은 위에 모두 적혀 있습니다.",
    voice_intro: "AI 인터뷰어가 몇 가지 짧은 질문을 소리 내어 드립니다. 말로 답해 주세요.",
    prefer_not: "답변하지 않겠습니다",
    show_more: "더 보기",
    n_selected: "{n}개 선택됨",
    tap_then_send: "해당하는 항목을 모두 누른 뒤 보내세요",
    choose_one: "하나를 선택하세요",
    pick_number: "숫자를 선택하세요",
    pick_top: "가장 중요한 항목부터 선택하세요",
    ranked_so_far: "현재 순위: {list} — {n}순위를 선택하세요",
    card_where: "“{card}”은(는) 어디에 속하나요? ({total}개 중 {n}번째)",
    study_image: "연구 이미지",
    upload_image: "이미지 업로드",
    uploading: "업로드 중…",
    upload_failed: "업로드 실패 — 다시 시도하거나 링크를 붙여넣으세요.",
    didnt_catch: "잘 듣지 못했습니다. 다시 시도해 주세요.",
    heard: "인식됨 “{raw}” → “{answer}”",
    speaking: "말하는 중…",
    listening: "듣는 중… 탭하여 종료",
    listening_hands_free: "듣고 있어요. 그냥 말씀하세요. 마이크를 탭하면 음소거됩니다.",
    mic_hands_free: "마이크 켜기/끄기",
    transcribing: "받아쓰는 중…",
    voice_unavailable: "음성을 사용할 수 없습니다.",
    voice_hiccup: "음성 문제가 발생했습니다. 다시 시도해 주세요.",
    mic_unavailable: "마이크를 사용할 수 없습니다. 권한을 확인하세요.",
    recording_broken: "녹음이 중단되었습니다. 인터뷰는 계속되며 답변은 텍스트로 계속 저장됩니다.",
    "rec.notice.title": "녹음 및 녹화",
    "rec.notice.audio_required": "본인과 인터뷰어의 음성이 녹음됩니다. 참여하려면 반드시 필요합니다.",
    "rec.notice.video_required": "본인의 카메라 영상과 인터뷰어의 영상도 녹화됩니다. 참여하려면 반드시 필요합니다.",
    "rec.notice.audio_optional": "음성 녹음 (본인과 인터뷰어)",
    "rec.notice.video_optional": "영상 녹화 (본인의 카메라와 인터뷰어)",
    "rec.notice.video_needs_audio": "영상은 음성과 함께만 녹화할 수 있습니다.",
    "rec.notice.continue": "계속",
    "rec.notice.decline": "동의하지 않습니다",
    "rec.indicator.audio": "이 인터뷰는 녹음되고 있습니다. 본인과 인터뷰어의 음성은 연구팀을 위해 저장됩니다.",
    "rec.indicator.video": "이 인터뷰는 녹화되고 있습니다. 본인의 카메라와 마이크, 인터뷰어의 영상과 음성은 연구팀을 위해 저장됩니다.",
    "rec.indicator.paused": "녹화가 일시 중지되었습니다. 인터뷰가 계속되면 다시 시작됩니다.",
    conversation_ended: "이 대화는 종료되었습니다.",
    message_failed: "메시지를 보내지 못했습니다. 다시 시도해 주세요.",
    "reprompt.options": "다시 해 볼게요. 아래 선택지 중 하나를 골라 주세요.",
    "reprompt.multi_select": "다시 해 볼게요. 아래 선택지에서 골라 주세요.",
    "reprompt.scale": "다시 해 볼게요. 아래 척도에서 한 지점을 골라 주세요.",
    "reprompt.ranking": "다시 해 볼게요. 아래 항목을 모두 순서대로 배열해 주세요.",
    "reprompt.card_sort": "다시 해 볼게요. 모든 항목을 그룹 중 하나에 넣어 주세요.",
    "done.title": "모두 끝났습니다. 감사합니다.",
    "done.text": "남겨 주신 말씀은 이것을 만드는 팀에 바로 전달됩니다.",
    "done.all_questions_asked": "모든 질문에 답해 주셨습니다. 남겨 주신 말씀은 이것을 만드는 팀에 바로 전달됩니다.",
    "done.consent_declined": "괜찮습니다. 방문해 주셔서 감사합니다.",
    "done.disqualified": "관심 가져 주셔서 감사합니다! 이번 연구는 참여 대상이 아니었습니다.",
    "done.global_turn_cap": "이번에는 여기까지입니다. 시간 내주셔서 감사합니다.",
    "closed.survey_full.title": "이 연구는 마감되었습니다.",
    "closed.survey_full.text": "최대 응답 수에 도달했습니다. 관심 가져 주셔서 감사합니다!",
    "closed.start_failed.title": "인터뷰를 진행할 수 없습니다.",
    "closed.start_failed.text": "기술적인 문제가 발생했습니다. 몇 분 후에 다시 시도하시거나, 문제가 계속되면 연구 담당자에게 문의하세요.",
    "closed.quota_denied.title": "이 연구는 응답을 받지 않습니다.",
    "closed.quota_denied.text": "현재 새로운 응답을 받지 않습니다. 관심 가져 주셔서 감사합니다!",
    copy_link: "링크 복사",
    copied: "복사됨",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "연락처",
    "contact.name": "이름",
    "contact.optional": "선택 사항",
    "contact.email": "이메일",
    "contact.phone": "전화번호",
    "contact.email_placeholder": "you@example.com",
    "contact.phone_placeholder": "010-1234-5678",
    "contact.submit": "보내기",
    "contact.decline": "공유하지 않을래요",
    "contact.privacy": "답변과 별도로 보관됩니다.",
    "contact.shared": "연락처를 공유했습니다",
    "contact.declined": "공유하지 않음",
    "contact.err.invalid_name": "이름이 너무 깁니다.",
    "contact.err.invalid_email": "올바른 이메일 주소를 입력해 주세요.",
    "contact.err.invalid_phone": "올바른 전화번호를 입력해 주세요(7~15자리).",
    "contact.err.missing_email": "계속하려면 이메일 주소가 필요합니다.",
    "contact.err.missing_phone": "계속하려면 전화번호가 필요합니다.",
    "contact.err.empty": "이메일 주소나 전화번호를 입력해 주세요.",
    "contact.err.required": "이 연구에 참여하려면 연락처가 필요합니다.",
    "contact.err.failed": "연락처를 저장하지 못했습니다. 다시 시도해 주세요.",
    consent_suffix: "예 또는 아니요(또는 번호)로 답해 주세요:",
  },

  zh: {
    interview: "访谈",
    open_interview: "打开访谈",
    close_interview: "关闭访谈",
    type_answer: "输入您的回答…",
    your_answer: "您的回答",
    send: "发送",
    hold_to_talk: "按住说话",
    language: "语言",
    language_error: "无法更改语言，请重试。",
    starting: "正在开始您的访谈…",
    start_failed: "无法开始此访谈，请重试。",
    progress: "第 {n} 题，共 {total} 题",
    continue: "继续",
    start_interview: "开始访谈",
    listen: "收听",
    listen_stop: "停止",
    listen_failed: "无法朗读欢迎语——内容已全部写在上方。",
    voice_intro: "AI 访谈员会用语音向您提出几个简短的问题。请直接口头回答。",
    prefer_not: "我不想回答",
    show_more: "显示更多",
    n_selected: "已选择 {n} 项",
    tap_then_send: "选择所有适用项，然后发送",
    choose_one: "请选择一项",
    pick_number: "请选择一个数字",
    pick_top: "请先选择您的首选",
    ranked_so_far: "目前的排序：{list} —— 请选择第 {n} 位",
    card_where: "“{card}”属于哪一组？（第 {n} 个，共 {total} 个）",
    study_image: "研究图片",
    upload_image: "上传图片",
    uploading: "正在上传…",
    upload_failed: "上传失败——请重试或粘贴链接。",
    didnt_catch: "没有听清，请再试一次。",
    heard: "识别为“{raw}” → “{answer}”",
    speaking: "正在说话…",
    listening: "正在聆听… 点按结束",
    listening_hands_free: "正在聆听——直接说就行。点按麦克风可静音。",
    mic_hands_free: "打开或关闭麦克风",
    transcribing: "正在转写…",
    voice_unavailable: "语音不可用。",
    voice_hiccup: "语音出了点问题，请重试。",
    mic_unavailable: "麦克风不可用——请检查权限。",
    recording_broken: "录制已停止——访谈会继续，您的回答仍会以文字形式保存。",
    "rec.notice.title": "录制",
    "rec.notice.audio_required": "您和访谈员的音频将被录制。这是参与的必要条件。",
    "rec.notice.video_required": "您的摄像头画面和访谈员的视频也将被录制。这是参与的必要条件。",
    "rec.notice.audio_optional": "录制音频（您和访谈员的）",
    "rec.notice.video_optional": "录制视频（您的摄像头和访谈员的）",
    "rec.notice.video_needs_audio": "视频只能与音频一起录制。",
    "rec.notice.continue": "继续",
    "rec.notice.decline": "我不同意",
    "rec.indicator.audio": "本次访谈正在录制——您和访谈员的音频将保存给研究团队。",
    "rec.indicator.video": "本次访谈正在录制——您的摄像头和麦克风，以及访谈员的视频和音频，将保存给研究团队。",
    "rec.indicator.paused": "录制已暂停，访谈继续时将恢复录制。",
    conversation_ended: "此对话已结束。",
    message_failed: "消息发送失败，请重试。",
    "reprompt.options": "我们再试一次——请从下面的选项中选择一个。",
    "reprompt.multi_select": "我们再试一次——请从下面的选项中选择。",
    "reprompt.scale": "我们再试一次——请在下面的量表上选择一个点。",
    "reprompt.ranking": "我们再试一次——请将下面所有项目排好顺序。",
    "reprompt.card_sort": "我们再试一次——请将每个项目放入其中一个分组。",
    "done.title": "全部完成——谢谢您。",
    "done.text": "您的回答会直接交给正在打造这项产品的团队。",
    "done.all_questions_asked": "您已回答所有问题。您的回答会直接交给正在打造这项产品的团队。",
    "done.consent_declined": "没关系——感谢您的光临。",
    "done.disqualified": "感谢您的关注！这项研究不太适合您。",
    "done.global_turn_cap": "今天就到这里——感谢您抽出时间。",
    "closed.survey_full.title": "此研究已关闭。",
    "closed.survey_full.text": "已达到回答数量上限。感谢您的关注！",
    "closed.start_failed.title": "我们无法进行此访谈。",
    "closed.start_failed.text": "我们这边出现了技术问题。请几分钟后重试；如果问题持续，请联系研究组织者。",
    "closed.quota_denied.title": "此研究暂不接受回答。",
    "closed.quota_denied.text": "目前不接受新的回答。感谢您的关注！",
    copy_link: "复制链接",
    copied: "已复制",
    // the contact step's form (contact.ts)
    "contact.eyebrow": "联系方式",
    "contact.name": "姓名",
    "contact.optional": "选填",
    "contact.email": "电子邮件",
    "contact.phone": "电话",
    "contact.email_placeholder": "you@example.com",
    "contact.phone_placeholder": "138 0013 8000",
    "contact.submit": "提交",
    "contact.decline": "不想提供",
    "contact.privacy": "与您的回答分开保存。",
    "contact.shared": "已提供联系方式",
    "contact.declined": "选择不提供",
    "contact.err.invalid_name": "姓名太长。",
    "contact.err.invalid_email": "请输入有效的电子邮件地址。",
    "contact.err.invalid_phone": "请输入有效的电话号码（7–15 位数字）。",
    "contact.err.missing_email": "需要电子邮件地址才能继续。",
    "contact.err.missing_phone": "需要电话号码才能继续。",
    "contact.err.empty": "请输入电子邮件地址或电话号码。",
    "contact.err.required": "参与本研究需要提供联系方式。",
    "contact.err.failed": "无法保存您的联系方式，请重试。",
    consent_suffix: "请回复“是”或“否”（或对应的数字）：",
  },
};

/** The supported language `value` names — English name, native name or code
 *  (`pt-BR` → Portuguese) — or null. */
export function findLanguage(value: string | null | undefined): Language | null {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return null;
  const base = v.replace("_", "-").split("-")[0];
  return (
    LANGUAGES.find(
      (l) => l.name.toLowerCase() === v || l.native.toLowerCase() === v || l.code === base
    ) ?? null
  );
}

/** `key` in `code`'s table (English when it lacks one), `{name}` holes filled. */
export function translate(
  code: string,
  key: string,
  vars?: Record<string, string | number>
): string {
  const raw = STRINGS[code]?.[key] ?? STRINGS.en![key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Where the server's consent instructions begin in a welcome turn, in any
 *  language (a welcome may be re-issued in another after a switch), or -1. */
export function consentSuffixIndex(text: string): number {
  let best = -1;
  for (const table of Object.values(STRINGS)) {
    const marker = table.consent_suffix;
    if (!marker) continue;
    const i = text.indexOf(marker);
    if (i >= 0 && (best < 0 || i < best)) best = i;
  }
  return best;
}
